/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import type { RefinementProps } from '../types';

const mapColorNameToHex = (colorName: string): string | null => {
    const colorMap: Record<string, string> = {
        red: '#dc2626',
        blue: '#2563eb',
        green: '#16a34a',
        yellow: '#ca8a04',
        orange: '#ea580c',
        purple: '#9333ea',
        pink: '#db2777',
        brown: '#a16207',
        black: '#000000',
        white: '#ffffff',
        gray: '#6b7280',
        grey: '#6b7280',
        navy: '#1e3a8a',
        beige: '#f5f5dc',
        tan: '#d2b48c',
        gold: '#ffd700',
        silver: '#c0c0c0',
    };

    const normalized = colorName.toLowerCase().trim();
    return colorMap[normalized] || null;
};

export default function RefineColor({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    return (
        <ul className="mt-2 flex flex-col gap-1">
            {values.map((value) => {
                const color = mapColorNameToHex(value.value) || mapColorNameToHex(value.label || '');
                const isSelected = isFilterSelected(attributeId, value.value);
                const label = value.label || value.value;

                return (
                    <li key={`${attributeId}:${value.value}`}>
                        <button
                            type="button"
                            onClick={() => toggleFilter(attributeId, value.value)}
                            aria-pressed={isSelected}
                            className={cn(
                                'flex w-full cursor-pointer items-center gap-2 rounded-xs py-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                isSelected && 'font-semibold'
                            )}>
                            <span
                                className={cn(
                                    'relative inline-block size-4 shrink-0 rounded-full border',
                                    isSelected ? 'border-foreground/80' : 'border-border'
                                )}
                                style={{ backgroundColor: color || '#e5e7eb' }}
                                aria-hidden>
                                {isSelected && (
                                    <span
                                        className={cn(
                                            'absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full',
                                            color === '#ffffff' || !color ? 'bg-foreground/40' : 'bg-background'
                                        )}
                                    />
                                )}
                            </span>
                            <span className="truncate text-sm leading-5 text-foreground">
                                {label}
                                {value.hitCount !== undefined && (
                                    <span className="ml-1 tabular-nums text-muted-foreground">({value.hitCount})</span>
                                )}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
